'use client';

import { createContext, PropsWithChildren, useCallback, useMemo } from 'react';

import { ITEMS_PER_PAGE_DESKTOP, MAX_ITEMS_PER_PAGE, RANGE_FILTER_SECTIONS } from '@/constants/inventory';
import {
  convertFiltersToSearchParams,
  convertSortBy,
  getFiltersFromSearchParams,
  revertSortBy,
} from '@/tools/inventory';
import { BaseFilters, Filters, FilterSection, RangeFilterValue, SortBy } from '@/types/inventory';
import { useCustomSearchParams } from '@lamesarv-sdk/hooks';
import { InventoryField, InventoryFieldMap } from '@lamesarv-sdk/types';

export interface NewInventoryFiltersContextData {
  baseFilters: BaseFilters;
  filters: Filters;
  modelsByMake: Record<string, string[]>;
  toggleFilter: (section: FilterSection, value: string) => void;
  setRangeFilter: (section: FilterSection, value: RangeFilterValue) => void;
  clearFilters: () => void;
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  query: string;
  setQuery: (query: string) => void;
  perPage: number;
  setPerPage: (perPage: number) => void;
  page: number;
  setPage: (page: number) => void;
}

export const NewInventoryFiltersContext = createContext({} as NewInventoryFiltersContextData);

const FILTER_KEYS = new Set(Object.values(InventoryField).map((field) => InventoryFieldMap.get(field) || field));

interface NewInventoryFiltersProviderProps {
  baseFilters: BaseFilters;
  modelsByMake: Record<string, string[]>;
}

export const NewInventoryFiltersProvider = ({
  baseFilters,
  children,
  modelsByMake,
}: PropsWithChildren<NewInventoryFiltersProviderProps>) => {
  const { searchParams, setRawParams } = useCustomSearchParams();

  let page: number | null = parseInt(searchParams.get('page'));
  if (isNaN(page) || page < 1) {
    page = 1;
  }
  const setPage = useCallback(
    (newPage: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (newPage > 1) {
        newSearchParams.set('page', String(newPage));
      } else {
        newSearchParams.delete('page');
      }
      setRawParams(newSearchParams.toString());
    },
    [searchParams],
  );

  let perPage: number | null = parseInt(searchParams.get('perPage'));
  if (isNaN(perPage) || perPage < 1 || perPage > MAX_ITEMS_PER_PAGE) {
    perPage = ITEMS_PER_PAGE_DESKTOP;
  }
  const setPerPage = useCallback(
    (newPerPage: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (newPerPage > 0 && newPerPage <= MAX_ITEMS_PER_PAGE) {
        newSearchParams.set('perPage', String(newPerPage));
      } else {
        newSearchParams.delete('perPage');
      }
      setRawParams(newSearchParams.toString());
    },
    [searchParams],
  );

  const sortBy = revertSortBy(searchParams.get('sortBy'));
  const setSortBy = useCallback(
    (newSortBy: SortBy) => {
      if (sortBy === newSortBy) return;

      const newSearchParams = new URLSearchParams(searchParams);

      if (newSortBy === SortBy.RECOMMENDED) {
        newSearchParams.delete('sortBy');
      } else {
        newSearchParams.set('sortBy', convertSortBy(newSortBy));
      }

      newSearchParams.delete('page');

      setRawParams(newSearchParams.toString());
    },
    [searchParams, sortBy],
  );

  const query = searchParams.get('q') || '';
  const setQuery = useCallback(
    (newQuery: string) => {
      if (query === newQuery) return;

      const newSearchParams = new URLSearchParams(searchParams);

      if (newQuery) {
        newSearchParams.set('q', newQuery);
      } else {
        newSearchParams.delete('q');
      }

      newSearchParams.delete('page');

      setRawParams(newSearchParams.toString());
    },
    [searchParams, query],
  );

  const filters = useMemo(() => getFiltersFromSearchParams(searchParams, baseFilters), [searchParams, baseFilters]);
  const setFilters = useCallback(
    (newFiltersDelta: Filters | ((prevFilters: Filters) => Filters)) => {
      const newFilters = typeof newFiltersDelta === 'function' ? newFiltersDelta(filters) : newFiltersDelta;
      const newSearchParams = convertFiltersToSearchParams(newFilters);

      searchParams.forEach((value, key) => {
        if (!FILTER_KEYS.has(key)) {
          newSearchParams.append(key, value);
        }
      });

      newSearchParams.delete('page');

      setRawParams(newSearchParams.toString());
    },
    [searchParams, filters],
  );

  const toggleFilter = useCallback(
    (section: FilterSection, value: string) => {
      setFilters((prevFilters) => {
        const currentValues = prevFilters[section] || [];

        if (RANGE_FILTER_SECTIONS.has(section) || !(currentValues instanceof Array)) return prevFilters;

        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];

        if (newValues.length === 0) {
          const newFilters = { ...prevFilters };
          delete newFilters[section];
          return newFilters;
        }

        return {
          ...prevFilters,
          [section]: newValues,
        };
      });
    },
    [setFilters],
  );

  const setRangeFilter = useCallback(
    (section: FilterSection, value?: RangeFilterValue | null) => {
      if (!RANGE_FILTER_SECTIONS.has(section)) return;

      setFilters((prevFilters) => {
        if (!value) {
          const newFilters = { ...prevFilters };
          delete newFilters[section];
          return newFilters;
        }

        return {
          ...prevFilters,
          [section]: value,
        };
      });
    },
    [setFilters],
  );

  const clearFilters = useCallback(() => {
    const newSearchParams = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (!FILTER_KEYS.has(key) && key !== 'sortBy' && key !== 'q' && key !== 'page') {
        newSearchParams.append(key, value);
      }
    });

    setRawParams(newSearchParams.toString());
  }, [searchParams]);

  const contextData = useMemo(
    (): NewInventoryFiltersContextData => ({
      modelsByMake,
      baseFilters,
      filters,
      toggleFilter,
      setRangeFilter,
      clearFilters,
      sortBy,
      setSortBy,
      query,
      setQuery,
      perPage,
      setPerPage,
      page,
      setPage,
    }),
    [
      modelsByMake,
      baseFilters,
      filters,
      toggleFilter,
      setRangeFilter,
      clearFilters,
      sortBy,
      setSortBy,
      query,
      setQuery,
      perPage,
      setPerPage,
      page,
      setPage,
    ],
  );

  return <NewInventoryFiltersContext.Provider value={contextData}>{children}</NewInventoryFiltersContext.Provider>;
};
